import { useState, useEffect, type FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { login, completeMFA, register, resendConfirmation, requestPasswordReset, startOAuth, type LoginMFAChallenge } from "@/lib/auth";
import { APIError } from "@/lib/api";
import { keys } from "@/lib/queries";
import { humanizeError, humanizeMFAError } from "@/lib/errorMessages";
import { validateEmail, validatePassword, PASSWORD_MIN_LENGTH } from "@/lib/validation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  PasswordInput,
  OtpInput,
  Field,
  Select,
  SegmentedControl,
} from "@/components/ui";
import { CurrencyMark } from "@/currency/CurrencyMark";

// Mirrors the server-side resend cooldown (EMAIL_CONFIRM_RESEND_COOLDOWN_MS /
// PASSWORD_RESET_RESEND_COOLDOWN_MS, default 60s). UI-only nicety — the server
// is the source of truth and silently no-ops within the window.
const RESEND_COOLDOWN_SECONDS = 60;

export type AuthMode = "login" | "register";

export interface AuthModalProps {
  open: boolean;
  mode: AuthMode;
  onOpenChange: (o: boolean) => void;
  onModeChange: (m: AuthMode) => void;
  onSuccess: () => void;
}

export function AuthModal({ open, mode, onOpenChange, onModeChange, onSuccess }: AuthModalProps) {
  const { t } = useTranslation("auth");
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<LoginMFAChallenge | null>(null);
  const [factorId, setFactorId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  // Per-field validation errors shown inline under each control (replacing the
  // browser's native "Please fill out this field." popups). Server/credential
  // errors stay in `err` at the form footer.
  const [fieldErrs, setFieldErrs] = useState<{ email: string | null; password: string | null }>({
    email: null,
    password: null,
  });
  const [busy, setBusy] = useState(false);
  // Bumped on a failed OTP attempt so the OtpInput remounts (clearing its
  // boxes and re-focusing the first one) for an immediate retry.
  const [mfaNonce, setMfaNonce] = useState(0);
  // When set, the modal shows the "confirm your email" panel instead of
  // the login/register form — either right after sign-up ("registered")
  // or because a sign-in hit the unconfirmed-email gate ("unconfirmed",
  // CLIENT_000209). The actual confirmation happens on the /confirm-email
  // route when the user clicks the magic link.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingReason, setPendingReason] = useState<"registered" | "unconfirmed" | "reset" | "merge">("registered");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendErr, setResendErr] = useState<string | null>(null);
  // When true, the modal shows the "request a reset link" sub-view,
  // entered from the login view's "Forgot password?" link.
  const [forgot, setForgot] = useState(false);
  // Seconds left before "resend" is allowed again — mirrors the server
  // cooldown so we disable the button and show a live countdown.
  const [cooldown, setCooldown] = useState(0);

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const reset = () => {
    setPassword("");
    setCode("");
    setChallenge(null);
    setFactorId("");
    setErr(null);
    setFieldErrs({ email: null, password: null });
    setBusy(false);
    setPendingEmail(null);
    setResendSent(false);
    setResendErr(null);
    setResendBusy(false);
    setForgot(false);
    setCooldown(0);
  };

  const finish = async () => {
    await qc.invalidateQueries({ queryKey: keys.me() });
    reset();
    onSuccess();
  };

  // Clear a field's inline error as soon as the user edits it, so a
  // correction feels immediate rather than waiting for the next submit.
  const clearFieldErr = (k: "email" | "password") =>
    setFieldErrs((p) => (p[k] === null ? p : { ...p, [k]: null }));

  // Switching tabs (Sign in ↔ Sign up) should drop any stale errors from the
  // other mode's submit.
  const handleModeChange = (m: AuthMode) => {
    setErr(null);
    setFieldErrs({ email: null, password: null });
    onModeChange(m);
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    const fe = { email: validateEmail(email), password: validatePassword(password) };
    setFieldErrs(fe);
    if (fe.email || fe.password) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await login({ email, password });
      if (res.kind === "needs_mfa") setChallenge(res.challenge);
      else await finish();
    } catch (e2) {
      // The hard email-confirmation gate (CLIENT_000209) is only reachable
      // with a correct password — show the resend panel instead of a
      // generic "wrong credentials" error.
      if (e2 instanceof APIError && e2.code === "CLIENT_000209") {
        setPendingReason("unconfirmed");
        setPendingEmail(email);
      } else {
        setErr(humanizeError(e2));
      }
    } finally {
      setBusy(false);
    }
  };

  // submitMFA takes the code explicitly so the OTP auto-submit (onComplete)
  // can pass the just-completed value without waiting for a state flush.
  const submitMFA = async (codeValue: string) => {
    if (!challenge || busy) return;
    const activeFactorId = factorId || challenge.factors[0]?.factor_id || "";
    const activeKind = (challenge.factors.find((f) => f.factor_id === activeFactorId)?.kind ?? "TOTP").toUpperCase();
    const trimmed = codeValue.trim();
    if (!trimmed) return;
    setErr(null);
    setBusy(true);
    try {
      await completeMFA({ challenge_id: challenge.challenge_id, factor_id: activeFactorId, code: trimmed });
      await finish();
    } catch (e2) {
      setErr(humanizeMFAError(e2, activeKind));
      // Reset the OTP boxes for a clean retry; free-text factors keep input.
      if (activeKind === "TOTP" || activeKind === "EMAIL_OTP") {
        setCode("");
        setMfaNonce((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleMFA = (e: FormEvent) => {
    e.preventDefault();
    void submitMFA(code);
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    const fe = {
      email: validateEmail(email),
      password: validatePassword(password, { min: PASSWORD_MIN_LENGTH, emptyMessage: t("validation.createPassword") }),
    };
    setFieldErrs(fe);
    if (fe.email || fe.password) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await register({ email, password, display_name: name || undefined });
      // Email confirmation is required before sign-in (hard gate), so we do
      // NOT auto-login — show the "check your email" panel. When the email
      // already belonged to a social account, the backend merges instead and
      // we show the merge panel.
      setPendingReason(res.merge_required ? "merge" : "registered");
      setPendingEmail(email);
      setCooldown(RESEND_COOLDOWN_SECONDS); // confirmation / merge email just sent
    } catch (e2) {
      setErr(humanizeError(e2));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail || resendBusy) return;
    setResendBusy(true);
    setResendErr(null);
    setResendSent(false);
    try {
      if (pendingReason === "reset") await requestPasswordReset(pendingEmail);
      else if (pendingReason === "merge") await register({ email: pendingEmail, password, display_name: name || undefined });
      else await resendConfirmation(pendingEmail);
      setResendSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e2) {
      setResendErr(humanizeError(e2));
    } finally {
      setResendBusy(false);
    }
  };

  const handleForgotRequest = async (e: FormEvent) => {
    e.preventDefault();
    const emailErr = validateEmail(email);
    setFieldErrs((p) => ({ ...p, email: emailErr }));
    if (emailErr) return;
    setErr(null);
    setBusy(true);
    try {
      // Enumeration-safe on the backend (always succeeds), so we always
      // advance to the "check your email" panel.
      await requestPasswordReset(email);
      setPendingReason("reset");
      setPendingEmail(email);
      setCooldown(RESEND_COOLDOWN_SECONDS); // reset email just sent
    } catch (e2) {
      setErr(humanizeError(e2));
    } finally {
      setBusy(false);
    }
  };

  const title = pendingEmail
    ? pendingReason === "unconfirmed"
      ? t("title.confirmEmail")
      : t("title.checkEmail")
    : forgot
      ? t("title.resetPassword")
      : challenge
        ? t("title.twoFactor")
        : mode === "login"
          ? t("title.welcomeBack")
          : t("title.createAccount");

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {/* Not a flex row: the tagline embeds the SPARK mark mid-sentence
              via <Trans>, and flex would make the icon a sibling of the text
              — so a longer translation (fil/vi) wraps the words to a second
              line while the icon stays pinned right, breaking reading order.
              Normal inline flow lets the mark wrap with the words. */}
          <DialogDescription>
            {pendingEmail ? (
              t("description.pending")
            ) : forgot ? (
              t("description.forgot")
            ) : challenge ? (
              t("description.mfa")
            ) : (
              <Trans
                t={t}
                i18nKey="description.tagline"
                components={{
                  spark: (
                    <CurrencyMark currency="GOLD" aria-hidden className="inline align-[-0.125em] text-primary" />
                  ),
                }}
              />
            )}
          </DialogDescription>
        </DialogHeader>

        {pendingEmail ? (
          <>
            <DialogBody className="space-y-3">
              <p className="text-sm text-fg-muted">
                <Trans
                  t={t}
                  i18nKey={
                    pendingReason === "registered"
                      ? "pending.registered"
                      : pendingReason === "reset"
                        ? "pending.reset"
                        : pendingReason === "merge"
                          ? "pending.merge"
                          : "pending.unconfirmed"
                  }
                  values={{ email: pendingEmail }}
                  components={{ em: <span className="font-medium text-fg" /> }}
                />
              </p>
              {resendSent ? (
                <p className="text-sm text-success">{t("pending.sent")}</p>
              ) : resendErr ? (
                <p className="text-sm text-danger">{resendErr}</p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <div className="flex w-full flex-col gap-2">
                <Button
                  type="button"
                  block
                  loading={resendBusy}
                  disabled={cooldown > 0}
                  onClick={handleResend}
                >
                  {cooldown > 0
                    ? t("pending.resendCountdown", { seconds: cooldown })
                    : pendingReason === "reset"
                      ? t("pending.resendReset")
                      : t("pending.resendConfirmation")}
                </Button>
                <Button
                  type="button"
                  block
                  variant="ghost"
                  onClick={() => {
                    setPendingEmail(null);
                    setResendSent(false);
                    setResendErr(null);
                    setForgot(false);
                    onModeChange("login");
                  }}
                >
                  {t("pending.backToSignIn")}
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : challenge ? (
          (() => {
            const sel =
              challenge.factors.find((f) => f.factor_id === (factorId || challenge.factors[0]?.factor_id)) ??
              challenge.factors[0];
            const kind = (sel?.kind ?? "TOTP").toUpperCase();
            const isOtp = kind === "TOTP" || kind === "EMAIL_OTP";
            const label =
              kind === "PASSPHRASE"
                ? t("mfa.labels.passphrase")
                : kind === "BACKUP_CODE"
                  ? t("mfa.labels.backupCode")
                  : kind === "EMAIL_OTP"
                    ? t("mfa.labels.emailOtp")
                    : t("mfa.labels.totp");
            // The EMAIL_OTP hint names no address: challenge.email is the
            // account's primary email, but the code goes to the address on
            // the factor (SM-832), which can differ.
            const hint =
              kind === "PASSPHRASE"
                ? t("mfa.hints.passphrase")
                : kind === "BACKUP_CODE"
                  ? t("mfa.hints.backupCode")
                  : kind === "EMAIL_OTP"
                    ? t("mfa.hints.emailOtp")
                    : t("mfa.hints.totp");
            return (
              <form onSubmit={handleMFA} noValidate>
                <DialogBody className="space-y-3">
                  {challenge.factors.length > 1 && (
                    <Field label={t("mfa.verifyWith")} htmlFor="mfa-factor">
                      <Select
                        id="mfa-factor"
                        value={factorId || challenge.factors[0].factor_id}
                        onValueChange={(v) => {
                          setFactorId(v);
                          setCode("");
                          setErr(null);
                        }}
                        options={challenge.factors.map((f) => ({ value: f.factor_id, label: f.friendly_name || f.kind }))}
                      />
                    </Field>
                  )}
                  <Field label={label} htmlFor="mfa-code" hint={hint} error={err ?? undefined}>
                    {isOtp ? (
                      <OtpInput
                        key={`otp-${mfaNonce}`}
                        id="mfa-code"
                        value={code}
                        onChange={setCode}
                        onComplete={(v) => void submitMFA(v)}
                        invalid={!!err}
                        disabled={busy}
                        autoFocus
                      />
                    ) : kind === "PASSPHRASE" ? (
                      <PasswordInput
                        // Remount on a factor switch so the next passphrase
                        // starts hidden: PasswordInput owns its own reveal
                        // state and the dialog stays mounted across switches.
                        key={`pp-${factorId}`}
                        id="mfa-code"
                        autoComplete="off"
                        autoFocus
                        invalid={!!err}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={t("mfa.passphrasePlaceholder")}
                        showLabel={t("mfa.showPassphrase")}
                        hideLabel={t("mfa.hidePassphrase")}
                      />
                    ) : (
                      <Input
                        id="mfa-code"
                        type="text"
                        autoComplete="off"
                        autoFocus
                        invalid={!!err}
                        value={code}
                        onChange={(e) => setCode(e.target.value.toLowerCase())}
                        placeholder="xxxx-xxxx"
                        maxLength={9}
                        className="text-center font-mono text-base tracking-[0.3em]"
                      />
                    )}
                  </Field>
                </DialogBody>
                <DialogFooter>
                  <Button type="submit" block loading={busy}>
                    {t("mfa.verify")}
                  </Button>
                </DialogFooter>
              </form>
            );
          })()
        ) : forgot ? (
          <form onSubmit={handleForgotRequest} noValidate>
            <DialogBody className="space-y-3 pt-3">
              <p className="text-sm text-fg-muted">{t("forgot.body")}</p>
              <Field label={t("fields.email")} htmlFor="au-email" error={fieldErrs.email ?? undefined}>
                <Input
                  id="au-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearFieldErr("email");
                  }}
                  placeholder={t("fields.emailPlaceholder")}
                  invalid={!!fieldErrs.email}
                  required
                />
              </Field>
              {err && <p className="text-sm text-danger">{err}</p>}
            </DialogBody>
            <DialogFooter>
              <div className="flex w-full flex-col gap-2">
                <Button type="submit" block loading={busy}>
                  {t("forgot.send")}
                </Button>
                <Button
                  type="button"
                  block
                  variant="ghost"
                  onClick={() => {
                    setForgot(false);
                    setErr(null);
                    setFieldErrs({ email: null, password: null });
                  }}
                >
                  {t("pending.backToSignIn")}
                </Button>
              </div>
            </DialogFooter>
          </form>
        ) : (
          <>
            <div className="px-5">
              <SegmentedControl
                block
                value={mode}
                onChange={handleModeChange}
                segments={[
                  { value: "login", label: t("tabs.signIn") },
                  { value: "register", label: t("tabs.signUp") },
                ]}
              />
            </div>
            <div className="px-5 pt-3">
              <Button
                type="button"
                variant="outline"
                block
                onClick={() => startOAuth("google", "login")}
              >
                <GoogleMark className="h-4 w-4" />
                {t("continueWithGoogle")}
              </Button>
              <div className="my-3 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-fg-muted">{t("or")}</span>
                <span className="h-px flex-1 bg-border" />
              </div>
            </div>
            <form onSubmit={mode === "login" ? handleLogin : handleRegister} noValidate>
              <DialogBody className="space-y-3 pt-3">
                {mode === "register" && (
                  <Field label={t("fields.displayName")} htmlFor="au-name">
                    <Input
                      id="au-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("fields.displayNamePlaceholder")}
                    />
                  </Field>
                )}
                <Field label={t("fields.email")} htmlFor="au-email" error={fieldErrs.email ?? undefined}>
                  <Input
                    id="au-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearFieldErr("email");
                    }}
                    placeholder={t("fields.emailPlaceholder")}
                    invalid={!!fieldErrs.email}
                    required
                  />
                </Field>
                <Field
                  label={t("fields.password")}
                  htmlFor="au-pass"
                  error={fieldErrs.password ?? undefined}
                  hint={
                    mode === "register" && !fieldErrs.password
                      ? t("fields.passwordHint", { count: PASSWORD_MIN_LENGTH })
                      : undefined
                  }
                >
                  <PasswordInput
                    id="au-pass"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearFieldErr("password");
                    }}
                    placeholder="••••••••"
                    invalid={!!fieldErrs.password}
                    required
                  />
                </Field>
                {mode === "login" && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-xs text-fg-muted hover:text-fg"
                      onClick={() => {
                        setForgot(true);
                        setErr(null);
                        setFieldErrs({ email: null, password: null });
                      }}
                    >
                      {t("forgotPassword")}
                    </button>
                  </div>
                )}
                {err && <p className="text-sm text-danger">{err}</p>}
              </DialogBody>
              <DialogFooter>
                <Button type="submit" block loading={busy}>
                  {mode === "login" ? t("tabs.signIn") : t("createAccount")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// GoogleMark is the official multi-color "G" used on the social sign-in
// button. Inlined (rather than added to the icon set) since it must keep
// its brand colors regardless of theme.
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
